import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LockReset as LockResetIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  Article as ArticleIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';
import KvkkApprovalModal from './KvkkApprovalModal';
import api, { handleApiError } from '../utils/api';
import { useTranslation } from 'react-i18next';

const ProfileSettings = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, updateUser } = useAuth();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    bio: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    },
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState(null);
  const [userApprovals, setUserApprovals] = useState([]);

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // KVKK content viewing modal state
  const [contentModal, setContentModal] = useState({ open: false, config: null });

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setLoading(true);
        // Use existing user data and potentially fetch more details if needed
        setFormData({
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          bio: user?.bio || '',
          emergencyContact: user?.emergencyContact || {
            name: '',
            relationship: '',
            phone: ''
          },
        });
      } catch (error) {
        console.error('Error loading profile:', error);
        setError(t('profile.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadUserProfile();
      loadUserApprovals();
    }
  }, [user, t]);

  const loadUserApprovals = async () => {
    try {
      const response = await api.get('/kvkk/my-approvals');
      if (response.data.success) {
        setUserApprovals(response.data.data);
      }
    } catch (error) {
      console.error('Error loading KVKK approvals:', error);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear messages when user interacts
    setError('');
    setSuccess('');
  };

  // Check if sensitive data is being changed that requires KVKK approval
  const requiresKvkkApproval = (currentData, newData) => {
    const sensitiveFields = [
      'emergencyContact.name',
      'emergencyContact.phone',
      'bio'
    ];

    return sensitiveFields.some(field => {
      const [parent, child] = field.includes('.') ? field.split('.') : [field, null];

      if (child) {
        return currentData[parent]?.[child] !== newData[parent]?.[child];
      } else {
        return currentData[parent] !== newData[parent];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if KVKK approval is needed for sensitive data changes
    if (requiresKvkkApproval(user, formData)) {
      // Store pending updates and show KVKK modal
      setPendingUpdates(formData);
      setShowKvkkModal(true);
      return;
    }
    
    // If no sensitive data changed, proceed directly
    await saveProfile(formData);
  };

  const saveProfile = async (profileData) => {
    setSaving(true);
    setError('');
    
    try {
      // Exclude firstName and lastName from profile updates (they're not editable)
      const { firstName, lastName, ...updateData } = profileData;
      const response = await api.put('/registration/update-profile', updateData);
      
      if (response.data.success) {
        setSuccess(t('profile.messages.profileUpdated'));
        
        // Update auth context
        if (updateUser) {
          updateUser(response.data.data);
        }
        
        // Reload approvals to show new ones
        await loadUserApprovals();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleKvkkApproval = async () => {
    if (!pendingUpdates) {
      setError('Pending update not found. Please try again.');
      return;
    }

    // Proceed with the profile update after KVKK approval
    await saveProfile(pendingUpdates);
    setPendingUpdates(null);
  };

  const handleKvkkModalClose = () => {
    setShowKvkkModal(false);
    setPendingUpdates(null);
  };

  // Password change handlers
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear messages when user interacts
    setPasswordError('');
    setPasswordSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return t('changePassword.messages.minLength', { ns: 'auth' });
    }
    if (!hasUpperCase) {
      return t('changePassword.messages.uppercase', { ns: 'auth' });
    }
    if (!hasLowerCase) {
      return t('changePassword.messages.lowercase', { ns: 'auth' });
    }
    if (!hasNumbers) {
      return t('changePassword.messages.number', { ns: 'auth' });
    }
    if (!hasSpecialChar) {
      return t('changePassword.messages.special', { ns: 'auth' });
    }
    return null;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    const { currentPassword, newPassword, confirmPassword } = passwordData;

    // Basic validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('changePassword.messages.allFieldsRequired', { ns: 'auth' }));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('changePassword.messages.passwordsDoNotMatch', { ns: 'auth' }));
      return;
    }

    // Validate new password strength
    const passwordValidation = validatePassword(newPassword);
    if (passwordValidation) {
      setPasswordError(passwordValidation);
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError(t('changePassword.messages.mustBeDifferent', { ns: 'auth' }));
      return;
    }

    setChangingPassword(true);
    setPasswordError('');

    try {
      const response = await api.put('/auth/change-password', {
        currentPassword,
        newPassword
      });

      if (response.data.success) {
        setPasswordSuccess(t('changePassword.messages.success', { ns: 'auth' }));
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(handleApiError(error));
    } finally {
      setChangingPassword(false);
    }
  };

  // KVKK content viewing handlers
  const handleViewContent = (checkboxResponse) => {
    setContentModal({
      open: true,
      config: checkboxResponse.configurationId
    });
  };

  const handleCloseContentModal = () => {
    setContentModal({ open: false, config: null });
  };

  const sanitizeHtml = (html) => {
    return {
      __html: DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        ALLOWED_ATTR: ['class']
      })
    };
  };

  const formatApprovalType = (type) => {
    return t(`kvkkApprovals.approvalTypes.${type}`, { ns: 'profile' });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {t('loading.default', { ns: 'common' })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="primary" />
        Profile Settings
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update your personal information and preferences. KVKK approval required for sensitive data changes.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Main Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  <Grid xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('register.form.firstName.label', { ns: 'auth' })}
                      </Typography>
                      <Typography variant="body1" sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        minHeight: '56px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {formData.firstName}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('register.form.lastName.label', { ns: 'auth' })}
                      </Typography>
                      <Typography variant="body1" sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        minHeight: '56px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {formData.lastName}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid xs={12}>
                    <TextField
                      label={t('labels.email', { ns: 'common' })}
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      fullWidth
                      type="email"
                      required
                      disabled // Email usually shouldn't be changed easily
                      helperText={t('profile.settings.email.helperText', { ns: 'profile' })}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <TextField
                      label={t('sections.bio', { ns: 'trainerProfile' })}
                      value={formData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder={t('profile.settings.bioPlaceholder', { ns: 'profile' })}
                      helperText={t('profile.settings.bio.helperText', { ns: 'profile' })}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Emergency Contact */}
              <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon color="primary" />
                  {t('sections.emergencyContact', { ns: 'registration' })}
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  <Grid xs={12} sm={6}>
                    <TextField
                      label={t('fields.contactName', { ns: 'registration' })}
                      value={formData.emergencyContact.name}
                      onChange={(e) => handleInputChange('emergencyContact.name', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <TextField
                      label={t('profile.settings.relationship.label', { ns: 'profile' })}
                      value={formData.emergencyContact.relationship}
                      onChange={(e) => handleInputChange('emergencyContact.relationship', e.target.value)}
                      fullWidth
                      placeholder={t('profile.settings.relationshipPlaceholder', { ns: 'profile' })}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <TextField
                      label={t('labels.phone', { ns: 'common' })}
                      value={formData.emergencyContact.phone}
                      onChange={(e) => handleInputChange('emergencyContact.phone', e.target.value)}
                      fullWidth
                      placeholder={t('placeholders.phoneExample', { ns: 'registration' })}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Submit Button */}
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  size="large"
                >
                  {saving ? t('loading.saving', { ns: 'common' }) : 'Save Changes'}
                </Button>
              </Box>
            </form>
          </Paper>

      {/* Password Change Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockResetIcon color="primary" />
              {t('changePassword.title', { ns: 'auth' })}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
            {passwordSuccess && <Alert severity="success" sx={{ mb: 2 }}>{passwordSuccess}</Alert>}

            <form onSubmit={handlePasswordSubmit}>
              <Grid container spacing={2}>
                <Grid xs={12}>
                  <TextField
                    label={t('changePassword.fields.currentPassword', { ns: 'auth' })}
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    fullWidth
                    required
                    slotProps={{ input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('current')}
                            edge="end"
                          >
                            {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}}
                  />
                </Grid>
                <Grid xs={12} sm={6}>
                  <TextField
                    label={t('changePassword.fields.newPassword', { ns: 'auth' })}
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    fullWidth
                    required
                    helperText={t('changePassword.helperText.requirements', { ns: 'auth' })}
                    slotProps={{ input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('new')}
                            edge="end"
                          >
                            {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}}
                  />
                </Grid>
                <Grid xs={12} sm={6}>
                  <TextField
                    label={t('changePassword.fields.confirmPassword', { ns: 'auth' })}
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    fullWidth
                    required
                    slotProps={{ input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => togglePasswordVisibility('confirm')}
                            edge="end"
                          >
                            {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={changingPassword}
                  startIcon={changingPassword ? <CircularProgress size={20} /> : <LockResetIcon />}
                  color="warning"
                >
                  {changingPassword ? t('changePassword.buttons.changing', { ns: 'auth' }) : t('changePassword.buttons.change', { ns: 'auth' })}
                </Button>
              </Box>
            </form>
          </Paper>

      {/* KVKK Approvals History Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon color="primary" />
              {t('kvkkApprovals.title', { ns: 'profile' })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('kvkkApprovals.description', { ns: 'profile' })}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {userApprovals.length === 0 ? (
              <Box textAlign="center" py={4}>
                <SecurityIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {t('kvkkApprovals.empty', { ns: 'profile' })}
                </Typography>
              </Box>
            ) : (
              <Box>
                {userApprovals.map((approval, index) => (
                  <Accordion key={approval._id} sx={{ mb: 2 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: 'background.default',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                        <SecurityIcon color="primary" />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {formatApprovalType(approval.approvalType)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                            <Chip
                              icon={<CalendarIcon />}
                              label={new Date(approval.approvedAt).toLocaleDateString(t('locale', { ns: 'common' }) || 'tr-TR')}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              icon={approval.summary.fullyAccepted ? <CheckCircleIcon /> : <InfoIcon />}
                              label={t('kvkkApprovals.summary.acceptedCount', {
                                ns: 'profile',
                                acceptedCount: approval.summary.acceptedCount,
                                totalCount: approval.summary.totalCount
                              })}
                              size="small"
                              color={approval.summary.fullyAccepted ? 'success' : 'warning'}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                          {t('kvkkApprovals.labels.acceptedItems', { ns: 'profile' })}:
                        </Typography>
                        <List dense>
                          {approval.checkboxResponses.map((response, idx) => (
                            <ListItem
                              key={idx}
                              sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1,
                                bgcolor: response.accepted ? 'success.50' : 'grey.50'
                              }}
                            >
                              <ListItemIcon>
                                {response.accepted ? (
                                  <CheckCircleIcon color="success" />
                                ) : (
                                  <CancelIcon color="error" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={response.title}
                                secondary={
                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {response.accepted
                                        ? t('kvkkApprovals.labels.accepted', { ns: 'profile' })
                                        : t('kvkkApprovals.labels.rejected', { ns: 'profile' })}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      • {new Date(response.acceptedAt).toLocaleString(t('locale', { ns: 'common' }) || 'tr-TR')}
                                    </Typography>
                                  </Box>
                                }
                              />
                              <Button
                                size="small"
                                startIcon={<ArticleIcon />}
                                onClick={() => handleViewContent(response)}
                                sx={{ ml: 1 }}
                              >
                                {t('kvkkApprovals.labels.viewContent', { ns: 'profile' })}
                              </Button>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Paper>

      {/* KVKK Content Viewing Modal */}
      <Dialog
        open={contentModal.open}
        onClose={handleCloseContentModal}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            <Typography variant={isMobile ? 'h6' : 'h5'} component="div" sx={{ fontWeight: 600 }}>
              {contentModal.config?.title}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseContentModal}
            size="small"
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          {contentModal.config && (
            <>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={contentModal.config.contentType === 'pdf'
                    ? t('labels.contentTypePdf', { ns: 'kvkk' })
                    : t('labels.contentTypeHtml', { ns: 'kvkk' })}
                  color="secondary"
                  size="small"
                />
              </Box>

              {contentModal.config.contentType === 'pdf' ? (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    height: isMobile ? 400 : 600,
                    overflow: 'hidden'
                  }}
                >
                  <iframe
                    src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${contentModal.config.pdfUrl}#toolbar=0`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }}
                    title={contentModal.config.title}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    '& p': { mb: 1 },
                    '& ul, & ol': { pl: 2, mb: 1 },
                    '& li': { mb: 0.5 },
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%'
                  }}
                  dangerouslySetInnerHTML={sanitizeHtml(contentModal.config.content)}
                />
              )}
            </>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseContentModal}
            variant="contained"
          >
            {t('buttons.close', { ns: 'kvkk' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* KVKK Approval Modal */}
      <KvkkApprovalModal
        open={showKvkkModal}
        onClose={handleKvkkModalClose}
        onApproved={handleKvkkApproval}
        context="profile_update"
        title="KVKK Approvals for Profile Update"
      />
    </Box>
  );
};

export default ProfileSettings;